import moment from 'moment';

/**
 * Helpers for the `enrollments` array the API returns for a patient/group pair.
 *
 * A patient can hold several enrollment instances in the same group (enrol, leave,
 * re-enrol), so `clientsEnrolled` and `getPatientPrograms` return one row per
 * patient/group with every instance aggregated as
 * `{ id, startDate, endDate, isActive }`. The API's reviver strips falsy values, so an
 * open-ended instance arrives with no `endDate` key at all and `isActive: false` is
 * dropped — membership is therefore derived from the dates, never from `isActive`.
 *
 * End dates are inclusive across this app: an enrollment is still current ON its end
 * date and has only ended once that date is strictly before today.
 */

export const CURRENT = 'current';
export const FORMER = 'former';

const toArray = (value) => (Array.isArray(value) ? value : []);

const timeOf = (date) => (date ? moment(date).valueOf() : 0);

/** True once the (inclusive) end date is strictly in the past. */
export const hasEnded = (enrollment) =>
	Boolean(enrollment && enrollment.endDate) &&
	moment(enrollment.endDate).isBefore(moment(), 'day');

/** True while the enrollment is open-ended or still within its end date. */
export const isCurrent = (enrollment) => Boolean(enrollment) && !hasEnded(enrollment);

/** The instance a "currently enrolled" list should show: the latest-starting open one. */
export const currentEnrollment = (enrollments) =>
	toArray(enrollments)
		.filter(isCurrent)
		.sort((a, b) => timeOf(a.startDate) - timeOf(b.startDate))
		.pop() || null;

/** The instance a "former" list should show: the most recently ended one. */
export const lastEndedEnrollment = (enrollments) =>
	toArray(enrollments)
		.filter(hasEnded)
		.sort((a, b) => timeOf(a.endDate) - timeOf(b.endDate))
		.pop() || null;

/** The single instance that represents a patient/group in the given list, if any. */
export const selectEnrollment = (enrollments, listType) =>
	listType === FORMER
		? lastEndedEnrollment(enrollments)
		: currentEnrollment(enrollments);

/**
 * At most one row per entity per list: rows with no instance in the requested state are
 * dropped, and the survivors carry the instance to display as `enrollment`. An entity
 * with both a current and a past instance appears in both lists — that is intended.
 */
export const rowsForEnrollmentList = (
	rows,
	listType,
	getEnrollments = (row) => row.enrollments,
) =>
	toArray(rows).reduce((kept, row) => {
		const enrollment = selectEnrollment(getEnrollments(row), listType);

		if (enrollment) {
			kept.push({ ...row, enrollment });
		}

		return kept;
	}, []);
